import {Button} from '@finpay-development/shared-components';
import {Grid, Paper} from '@mui/material';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import AccessControl from '@finpay-development/shared-platform/lib/esm/security/components/access-control';
import {RolePageNames} from '@finpay-development/shared-platform/lib/esm/security/model/role-page-names';
import {configSettings} from '@finpay-development/shared-platform/lib/esm/shared/configuration/config-settings';
import {patientService} from '@finpay-development/shared-platform/lib/esm/patient/services/patient-service';
import {
    getInstanceOfCare,
    savePatientStatus,
} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import {
    showErrorStatus,
    showStatus,
} from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import {Workflow} from '@finpay-development/shared-platform/lib/esm/shared/model/client';
import {ChangeStatusModal} from '../../patient/components/patient-overview/change-status-modal';
import ScriptButton from '../../patient/components/patient-drilldown/patient-details/script-button';
import {SendToMobileQueueErrorModal} from '../../patient/components/patient-drilldown/patient-details/send-to-mobile-queue-error-modal';
import {RootState} from '../../shared/state/root-reducer';
import {AppDispatch} from '../../shared/state/store';

const mobilePaymentWorkflowSubStatusIds = [60, 61, 62, 63, 64, 65];

export function EstimatesFooter() {
    const dispatch = useDispatch<AppDispatch>();
    const [isChangeStatusModalOpen, setChangeStatusModalOpen] = useState(false);
    const [
        isSendToMobileQueueErrorModalOpen,
        setIsSendToMobileQueueErrorModalOpen,
    ] = useState(false);
    const [
        sendToMobileQueueRequirementsList,
        setSendToMobileQueueRequirementsList,
    ] = useState<string[]>([]);

    const selectedEncounter = useSelector(
        (state: RootState) => state.patientContext.selectedEncounter
    );
    const patientEncounters = useSelector(
        (state: RootState) => state.patientContext.selectedPatient?.patientEncounters
    );
    const isLoading = useSelector(
        (state: RootState) => state.patientContext.isLoading.patientDemographics
    );
    const isLoadingIOC = useSelector(
        (state: RootState) => state.patientContext.isLoading.patientIOC
    );

    const actionsDisabled =
        isLoading || isLoadingIOC || !selectedEncounter?.patientEncounterId;

    const displaySendToMobileButton =
        selectedEncounter?.workflow?.workflowId !== 3 &&
        !mobilePaymentWorkflowSubStatusIds.includes(
            +selectedEncounter?.workflow?.workflowSubStatus?.workflowSubStatusId
        );

    const handleStatusChange = () => {
        if (patientEncounters || selectedEncounter?.patientEncounterId) {
            setChangeStatusModalOpen(true);
        }
    };

    const handleChangeStatusModalCancel = () => {
        setChangeStatusModalOpen(false);
    };

    const handleChangeStatusModalSubmit = async (value: {
        workFlow: Workflow;
        encounterId: number;
        patientId: number;
    }) => {
        await dispatch(savePatientStatus(value));
        setChangeStatusModalOpen(false);
    };

    const handleSendToMobileModalCancel = () => {
        setIsSendToMobileQueueErrorModalOpen(false);
    };

    const handleSendToMobile = async () => {
        const response = await patientService.sendInstanceOfCareToMobile(
            selectedEncounter.patientEncounterId
        );

        if (!response.hasErrors) {
            setSendToMobileQueueRequirementsList([]);
            dispatch(showStatus('IOC Sent to Mobile'));
            await dispatch(
                getInstanceOfCare({
                    patientId: selectedEncounter.patientId,
                    encounterId: selectedEncounter.patientEncounterId,
                })
            );
        } else if (
            Array.isArray(response.entity?.failureMessages) &&
            response.entity?.failureMessages.length > 0
        ) {
            setSendToMobileQueueRequirementsList(response.entity.failureMessages);
            setIsSendToMobileQueueErrorModalOpen(true);
        } else if (
            response.statusCode &&
            response.statusCode >= 400 &&
            response.statusCode < 500
        ) {
            dispatch(showErrorStatus(response.entity.message));
        } else {
            dispatch(
                showErrorStatus(
                    'Something went wrong while sending mobile engagement. Please reach out to the tech team for support.'
                )
            );
        }
    };

    return (
        <>
            <Paper
                sx={{position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2}}
                elevation={3}
                test-id="estimates-footer"
            >
                <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    className="py-4"
                >
                    <Grid item>
                        {displaySendToMobileButton && (
                            <AccessControl
                                rolePageName={RolePageNames.PatientRecords}
                                actionName="Add Instance of Care"
                                renderNoAccess={() => (
                                    <Button
                                        type="secondary"
                                        marginRight={6}
                                        disabled
                                    >
                                        Send to Mobile
                                    </Button>
                                )}
                            >
                                <Button
                                    type="secondary"
                                    marginRight={6}
                                    disabled={actionsDisabled}
                                    onClick={() => handleSendToMobile()}
                                    test-id="send-to-mobile-btn"
                                >
                                    Send to Mobile
                                </Button>
                            </AccessControl>
                        )}
                        <AccessControl
                            rolePageName={RolePageNames.PatientRecords}
                            actionName="Change Status"
                            renderNoAccess={() => (
                                <Button disabled marginRight={6}>
                                    Change Status
                                </Button>
                            )}
                        >
                            <Button
                                onClick={() => handleStatusChange()}
                                type="secondary"
                                disabled={actionsDisabled}
                                marginRight={6}
                                test-id="footer-change-status-button"
                            >
                                Change Status
                            </Button>
                        </AccessControl>
                        <ScriptButton
                            selectedEncounter={selectedEncounter}
                            disable={actionsDisabled}
                            environment={configSettings.app_environment}
                        />
                    </Grid>
                </Grid>
                {isChangeStatusModalOpen && (
                    <ChangeStatusModal
                        open={isChangeStatusModalOpen}
                        handleModalCancel={handleChangeStatusModalCancel}
                        handleModalSubmit={handleChangeStatusModalSubmit}
                    />
                )}
            </Paper>
            <SendToMobileQueueErrorModal
                isOpen={isSendToMobileQueueErrorModalOpen}
                handleModalCancel={handleSendToMobileModalCancel}
                requirementList={sendToMobileQueueRequirementsList}
            />
        </>
    );
}
